import React, { useEffect, useRef, useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MapPin, Briefcase, Calendar, ArrowLeft, CheckCircle2 } from 'lucide-react';
import CareerLayout from './CareerLayout';
import CareerApplyForm from './CareerApplyForm';
import { getTenantHeader, getJob } from '../../services/careerService';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  text-decoration: none;
  margin-bottom: 16px;
  &:hover { color: ${({ theme }) => theme.colors.primary.main}; }
`;

const Title = styled.h1`
  font-size: 28px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 28px;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 24px 28px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HtmlBlock = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  p { margin: 0 0 8px 0; }
  ul, ol { margin: 0 0 8px 0; padding-left: 22px; }
  ul { list-style: disc outside; }
  ol { list-style: decimal outside; }
  li { margin: 2px 0; }
  h2 { font-size: 16px; font-weight: 700; margin: 6px 0; }
  h3 { font-size: 14px; font-weight: 700; margin: 4px 0; }
  a { color: ${({ theme }) => theme.colors.primary.main}; text-decoration: underline; }
`;

// FE-36 — BAŞARI AFİŞİ UZUN SAYFANIN TEPESİNDEYDİ. İş tanımı + sorumluluklar +
// nitelikler + yan haklar kartlarından sonra gelen formu dolduran aday, "Gönder"e
// bastığında sayfanın EN ALTINDAdır. Afiş tepede belirir, form kaybolur, sayfa
// kısalır — adayın görüş alanında hiçbir şey değişmez. Müşteri geri bildirimi
// tam olarak buydu: "başvurdum mu, bilmiyorum" (ve mükerrer başvurular).
//
// Çözüm: afiş görünür alana KAYDIRILIR ve odak ona taşınır (`tabIndex={-1}`),
// `role="status"` ile de ekran okuyucuya duyurulur. `scrollIntoView` jsdom'da
// tanımsızdır — çağrı korumalı.
const SuccessBanner = styled.div`
  background: #E8F8F0;
  border: 1px solid #2EA44F;
  color: #1A6E38;
  padding: 16px 20px;
  border-radius: 10px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
  &:focus { outline: none; }
`;

const SuccessText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SuccessHeadline = styled.strong`
  font-weight: 700;
`;

const SuccessDetail = styled.span`
  font-size: 13px;
  line-height: 1.5;
`;

const fmtDate = (d, lang) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(lang || 'tr'); } catch { return null; }
};

const Section = ({ titleKey, html }) => {
  const { t } = useTranslation();
  if (!html) return null;
  return (
    <Card>
      <SectionTitle>{t(titleKey)}</SectionTitle>
      <HtmlBlock dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
    </Card>
  );
};

export const readInvitationToken = (search) => {
  try {
    return new URLSearchParams(search || '').get('inv') || null;
  } catch {
    return null;
  }
};

export default function CareerJobDetailPage() {
  const { tenantSlug, jobSlug } = useParams();
  const history = useHistory();
  // SPEC F2 — "Pozisyona Davet Et" e-postasındaki bağlantı `?inv=<token>` taşır.
  // İLK açılışta bir kez okunur ve saklanır: eski-slug düzeltmesi adresi
  // `history.replace` ile sorgusuz yeniden yazar, token o anda URL'den düşer.
  // (BrowserRouter adresi window.location ile eşit tutar; ilk çizimde okumak yeter.)
  const invitationTokenRef = useRef(readInvitationToken(window.location.search));
  const { t, i18n } = useTranslation();
  const [tenant, setTenant] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const successRef = useRef(null);
  // SPEC F3 — görüntülenme sayımı. Eski slug'la gelinip adres kanonik slug'a
  // `replace` edildiğinde `jobSlug` değişir ve efekt ilanı YENİDEN çeker; o ikinci
  // GET aynı ziyaretin devamıdır, SAYILMAMALI. Yönlendirmenin hedefi burada
  // tutulur: efekt o slug için başlıksız çeker, sonra işareti tüketir.
  const redirectedSlugRef = useRef(null);

  // Afiş çizildikten SONRA çalışır (submitted → true render'ının ardından).
  useEffect(() => {
    if (!submitted) return;
    const el = successRef.current;
    if (!el) return;
    el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    el.focus?.();
  }, [submitted]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const countView = redirectedSlugRef.current !== jobSlug;
    redirectedSlugRef.current = null;
    Promise.all([getTenantHeader(tenantSlug), getJob(tenantSlug, jobSlug, { countView })])
      .then(([tRes, jRes]) => {
        if (cancelled) return;
        const tData = tRes.data?.data;
        const jData = jRes.data?.data;
        if (!tData || !jData) { setNotFound(true); return; }
        setTenant(tData);
        setJob(jData);

        // KARAR 9 — ESKİ ADRESLE GELİNDİYSE ADRESİ DÜZELT.
        //
        // Sunucu ilanı ESKİ bir slug'tan bulduysa `canonicalSlug` döner. Sayfa
        // zaten çizildi (ziyaretçi bekletilmez); yalnız adres çubuğu sessizce
        // güncel adrese çevrilir.
        //
        // `replace` — `push` DEĞİL: geri tuşu ziyaretçiyi ölü adrese geri
        // götürmemeli, geldiği yere götürmeli.
        //
        // Bu bir HTTP 301 DEĞİLDİR ve olamaz: kariyer sayfası istemcide çizilir,
        // XHR'a dönen 301'i tarayıcı sessizce izler ve SPA onu hiç görmez
        // (bkz. CareerJobDetail.CanonicalSlug yorumu).
        if (jData.canonicalSlug && jData.canonicalSlug !== jobSlug) {
          redirectedSlugRef.current = jData.canonicalSlug;
          history.replace(`/career/${tenantSlug}/jobs/${jData.canonicalSlug}`);
        }
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantSlug, jobSlug, history]);

  if (loading) return <CareerLayout tenant={null}><p>{t('career.loading')}</p></CareerLayout>;
  if (notFound) return <CareerLayout tenant={null}><Card>{t('career.jobNotFound')}</Card></CareerLayout>;

  return (
    <CareerLayout tenant={tenant}>
      <BackLink to={`/career/${tenantSlug}/jobs`}>
        <ArrowLeft size={14} />{t('career.backToList')}
      </BackLink>
      <Title>{job.title}</Title>
      <Meta>
        {job.department && (<MetaItem><Briefcase size={14} />{job.department}</MetaItem>)}
        {job.location && (<MetaItem><MapPin size={14} />{job.location}</MetaItem>)}
        {job.employmentType && (<MetaItem>{t(`ats.requisition.employmentType.${job.employmentType}`, { defaultValue: job.employmentType })}</MetaItem>)}
        {job.remoteType && (<MetaItem>{t(`ats.requisition.remoteType.${job.remoteType}`, { defaultValue: job.remoteType })}</MetaItem>)}
        {job.postedAt && (<MetaItem><Calendar size={14} />{fmtDate(job.postedAt, i18n.language)}</MetaItem>)}
      </Meta>

      {submitted && (
        <SuccessBanner ref={successRef} role="status" tabIndex={-1} data-testid="apply-success">
          <CheckCircle2 size={20} />
          <SuccessText>
            <SuccessHeadline>{t('career.applySuccess')}</SuccessHeadline>
            <SuccessDetail>{t('career.applySuccessDetail')}</SuccessDetail>
          </SuccessText>
        </SuccessBanner>
      )}

      <Section titleKey="career.section.description" html={job.descriptionHtml} />
      <Section titleKey="career.section.responsibilities" html={job.responsibilitiesHtml} />
      <Section titleKey="career.section.requirements" html={job.requirementsHtml} />
      <Section titleKey="career.section.benefits" html={job.benefitsHtml} />

      {!submitted && (
        <CareerApplyForm
          tenantSlug={tenantSlug}
          jobSlug={jobSlug}
          jobTitle={job.title}
          companyName={tenant?.name}
          invitationToken={invitationTokenRef.current}
          onSuccess={() => setSubmitted(true)}
        />
      )}
    </CareerLayout>
  );
}
